import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { API } from "@/config";
import ClosuresPanel from "@/components/admin/ClosuresPanel";

const DAYS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
  { value: 0, label: "Domingo" },
];

const HOURS = Array.from({ length: 33 }, (_, i) => {
  const m = 6 * 60 + i * 30;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
});
const INTERVALS = [
  { value: 30, label: "Cada 30 min" },
  { value: 45, label: "Cada 45 min" },
  { value: 60, label: "Cada 1 hora" },
  { value: 90, label: "Cada 1 h 30 min" },
  { value: 120, label: "Cada 2 horas" },
];

const selectCls =
  "rounded-xl border border-sand bg-cream px-3 py-2 text-sm text-obsidian focus:outline-none focus:ring-2 focus:ring-gold disabled:opacity-40";

export default function SchedulePanel({ headers }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/schedule`)
      .then((res) => setCfg({ interval: res.data.interval, days: res.data.hours }))
      .catch(() => toast.error("No se pudo cargar el horario"));
  }, []);

  const setDay = (d, patch) =>
    setCfg((c) => ({ ...c, days: { ...c.days, [d]: { ...c.days[d], ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/schedule`, cfg, { headers });
      setCfg({ interval: res.data.interval, days: res.data.hours });
      toast.success("Horario actualizado en tu web");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar el horario");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="mt-6 h-48 rounded-2xl bg-sand/50 animate-pulse" />;

  const anyOpen = Object.values(cfg.days).some((d) => d.open);

  return (
    <section data-testid="admin-schedule-panel">
      <h1 className="font-display text-2xl sm:text-3xl font-semibold text-obsidian">Mi horario de trabajo</h1>
      <p className="text-sm text-taupe mt-1">
        Define tu horario habitual de cada día de la semana. Más abajo puedes cerrar días u horas concretas.
      </p>

      <div className="mt-6 rounded-3xl bg-creamcard border border-sand p-6 sm:p-8 grid gap-6">
        <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Horario semanal</p>
        <div className="grid gap-3">
          {DAYS.map((d) => {
            const day = cfg.days[d.value];
            return (
              <div
                key={d.value}
                data-testid={`schedule-row-${d.value}`}
                className={`grid grid-cols-[auto,1fr] sm:grid-cols-[120px,1fr] items-center gap-3 rounded-2xl border px-4 py-3 transition-colors ${
                  day.open ? "border-sand bg-cream" : "border-sand/60 bg-sand/20"
                }`}
              >
                <button
                  onClick={() => setDay(d.value, { open: !day.open })}
                  data-testid={`schedule-day-${d.value}`}
                  className={`rounded-full text-sm font-medium px-4 py-2 border transition-colors duration-200 ${
                    day.open ? "bg-gold text-white border-gold" : "bg-cream text-taupe border-sand hover:border-gold/50"
                  }`}
                >
                  {d.label}
                </button>
                {day.open ? (
                  <div className="flex flex-wrap items-center gap-2 text-sm text-cocoa">
                    <span>de</span>
                    <select
                      value={day.start}
                      onChange={(e) => setDay(d.value, { start: e.target.value })}
                      data-testid={`schedule-start-${d.value}`}
                      className={selectCls}
                    >
                      {HOURS.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    <span>a</span>
                    <select
                      value={day.end}
                      onChange={(e) => setDay(d.value, { end: e.target.value })}
                      data-testid={`schedule-end-${d.value}`}
                      className={selectCls}
                    >
                      {HOURS.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    <span className="text-xs text-taupe">(última cita)</span>
                  </div>
                ) : (
                  <span className="text-sm text-taupe">Cerrado</span>
                )}
              </div>
            );
          })}
        </div>

        <label className="grid gap-1.5 w-fit">
          <span className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Duración por cita</span>
          <select
            value={cfg.interval}
            onChange={(e) => setCfg({ ...cfg, interval: Number(e.target.value) })}
            data-testid="schedule-interval-select"
            className={selectCls}
          >
            {INTERVALS.map((i) => (
              <option key={i.value} value={i.value}>{i.label}</option>
            ))}
          </select>
        </label>

        <button
          onClick={save}
          disabled={saving || !anyOpen}
          data-testid="schedule-save-btn"
          className="inline-flex w-fit items-center gap-2 rounded-xl bg-gold hover:bg-gold-dark disabled:opacity-50 text-white text-sm font-medium px-8 py-3 transition-colors duration-200"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Guardar horario
        </button>
      </div>

      <ClosuresPanel headers={headers} />
    </section>
  );
}
